import { useEffect, useRef, useState } from 'react';

// A tenant who has never been scored used to see an empty page with no
// explanation and no way forward. Scoring takes about a minute (measured:
// 62s candidates, 41s outreach on 127,871 people), so the page polls while
// it waits rather than hanging or lying with a fake progress bar.
//
// Polls `refetch` every POLL_INTERVAL_MS while `state === 'computing'`, and
// stops the moment that stops being true -- the state moves on, or the
// ceiling below is hit. `refetch` is read through a ref so a new function
// identity on every render (the common case -- callers rarely useCallback
// it) does not restart the interval or reset the elapsed clock.

const POLL_INTERVAL_MS = 3000;

// Five times the measured worst case (62s). A `computing` state that has not
// resolved by then is not "a little slow" -- something is wrong, and a
// component polling forever in that case is its own bug.
export const CEILING_SECONDS = 300;

export function useScoringState(state, refetch) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (state !== 'computing') {
      setElapsedSeconds(0);
      return undefined;
    }

    let seconds = 0;
    setElapsedSeconds(0);

    const id = setInterval(() => {
      seconds += POLL_INTERVAL_MS / 1000;
      setElapsedSeconds(seconds);
      if (seconds >= CEILING_SECONDS) {
        clearInterval(id);
        return;
      }
      refetchRef.current();
    }, POLL_INTERVAL_MS);

    // Clears the interval on unmount -- a leaked interval polling a dead
    // component is exactly the bug this hook exists to avoid -- and also
    // whenever `state` changes away from 'computing' before the effect
    // above even runs again, since React always tears down the previous
    // effect first.
    return () => clearInterval(id);
  }, [state]);

  const isPolling = state === 'computing' && elapsedSeconds < CEILING_SECONDS;

  return { elapsedSeconds, isPolling };
}
